// Vizuální vrstva pexesa: velké karty, klepnutí, animace otočení. Bez hoveru.
// Pro klávesovou čtečku (NVDA) je skrytá (aria-hidden), ta používá zvukovou vrstvu.
// V režimu „dotyk se čtečkou“ (VoiceOver) jsou karty tlačítka s popisem a odmítnutí se ohlásí.
import type { GameEvent } from "../../../games/types.ts";
import { ANIMAL_BY_ID, type PexesoView } from "../../../games/pexeso/meta.ts";
import { say } from "../../announce.ts";
import { cardText, posText, rowCol, turnText, type Model } from "./model.ts";

const GAP = 8;

export class VisualLayer {
  readonly root: HTMLElement;
  private cards: HTMLElement[] = [];
  private builtFor = "";
  private resizeObs: ResizeObserver;
  showCursor = false;
  /** Grafika zpřístupněná pro dotykovou čtečku (VoiceOver). */
  private accessible = false;

  private model: Model;
  private board: HTMLElement;

  constructor(model: Model, board: HTMLElement) {
    this.model = model;
    this.board = board;
    this.root = document.createElement("div");
    this.root.className = "pexeso-grid";
    this.root.setAttribute("aria-hidden", "true");
    board.appendChild(this.root);
    this.root.addEventListener("click", (e) => {
      const el = (e.target as HTMLElement).closest<HTMLElement>(".card");
      if (el) this.tap(Number(el.dataset.index));
    });
    this.resizeObs = new ResizeObserver(() => this.layout());
    this.resizeObs.observe(board);
    window.addEventListener("orientationchange", this.layoutSoon);
  }

  private layoutSoon = () => setTimeout(() => this.layout(), 200);

  setAccessible(on: boolean) {
    this.accessible = on;
    if (on) {
      this.root.removeAttribute("aria-hidden");
      this.root.setAttribute("role", "group");
      this.root.setAttribute("aria-label", "Karty pexesa, řádky shora, sloupce zleva");
    } else {
      this.root.setAttribute("aria-hidden", "true");
      this.root.removeAttribute("role");
      this.root.removeAttribute("aria-label");
    }
    this.layout();
    this.render();
  }

  destroy() {
    this.resizeObs.disconnect();
    window.removeEventListener("orientationchange", this.layoutSoon);
    this.root.remove();
  }

  private tap(index: number) {
    const view = this.model.view;
    if (!view || view.over) return;
    const card = view.cards[index];
    if (view.turn !== this.model.me || view.pendingHide || card.state !== "down") {
      this.wiggle(this.cards[index]);
      // Kdo nevidí zatřesení, musí slyšet proč.
      if (this.accessible) {
        if (view.turn !== this.model.me) say(turnText(view, this.model.me));
        else if (view.pendingHide) say("Počkej, karty se zakrývají.");
        else say(`Tahle karta je ${cardText(card)}.`);
      }
      return;
    }
    this.model.cursor = index;
    this.model.sendAction({ type: "flip", index });
  }

  private wiggle(el: HTMLElement | undefined) {
    if (!el) return;
    el.classList.remove("wiggle");
    void el.offsetWidth;
    el.classList.add("wiggle");
  }

  private build(view: PexesoView) {
    this.root.textContent = "";
    this.cards = view.cards.map((_, i) => {
      const card = document.createElement("div");
      card.className = "card";
      card.dataset.index = String(i);
      card.innerHTML = '<div class="card-inner"><div class="card-face card-back"></div><div class="card-face card-front"><img alt=""><span class="card-name"></span></div></div>';
      this.root.appendChild(card);
      return card;
    });
    this.builtFor = `${view.rows}x${view.cols}`;
    this.layout();
  }

  render() {
    const view = this.model.view;
    if (!view) return;
    if (this.builtFor !== `${view.rows}x${view.cols}` || this.cards.length !== view.cards.length) this.build(view);
    view.cards.forEach((c, i) => {
      const el = this.cards[i];
      const open = c.state !== "down";
      el.classList.toggle("is-up", open);
      el.classList.toggle("is-matched", c.state === "matched");
      el.classList.toggle("is-cursor", this.showCursor && i === this.model.cursor);
      if (this.accessible) {
        el.setAttribute("role", "button");
        el.setAttribute("aria-label", `${posText(view, i)}, ${cardText(c)}`);
      } else {
        el.removeAttribute("role");
        el.removeAttribute("aria-label");
      }
      const owner = c.state === "matched" ? view.players.findIndex((p) => p.id === c.by) : -1;
      el.dataset.owner = owner >= 0 ? String(owner) : "";
      const img = el.querySelector("img")!;
      const name = el.querySelector<HTMLElement>(".card-name")!;
      if (open) {
        const animal = ANIMAL_BY_ID[c.animal];
        if (animal && img.getAttribute("src") !== `/img/pexeso/${animal.image}`) img.src = `/img/pexeso/${animal.image}`;
        name.textContent = animal?.name ?? "";
        el.style.setProperty("--animal-bg", animal?.color ?? "#fff");
      } else {
        // Obsah zakryté karty v DOM nesmí být (ani v obrázku).
        img.removeAttribute("src");
        name.textContent = "";
        el.style.removeProperty("--animal-bg");
      }
    });
    this.root.classList.toggle("my-turn", view.turn === this.model.me && !view.over);
  }

  onEvents(events: GameEvent[]) {
    for (const e of events) {
      if (e.type === "matched") for (const i of e.indices as number[]) this.flash(this.cards[i], "pop");
      if (e.type === "mismatched") for (const i of e.indices as number[]) this.flash(this.cards[i], "wiggle");
    }
  }

  private flash(el: HTMLElement | undefined, cls: string) {
    if (!el) return;
    el.classList.remove(cls);
    void el.offsetWidth;
    el.classList.add(cls);
  }

  /** Velikost karet podle místa; na výšku se široká mřížka otočí (sloupce ↔ řádky). */
  layout() {
    const view = this.model.view;
    if (!view || !this.cards.length) return;
    const rect = this.board.getBoundingClientRect();
    const availW = Math.max(100, rect.width);
    const availH = Math.max(100, window.innerHeight - (rect.top + window.scrollY) - 16);
    const portrait = availH > availW;
    // Pro dotykovou čtečku se mřížka neotáčí: řádek a sloupec v popisu musí sedět s místem na obrazovce.
    const transpose = !this.accessible && (portrait ? view.cols > view.rows : view.rows > view.cols);
    const dRows = transpose ? view.cols : view.rows;
    const dCols = transpose ? view.rows : view.cols;
    const ratio = 1.25; // výška / šířka karty
    const w = Math.floor(Math.min((availW - GAP * (dCols - 1)) / dCols, (availH - GAP * (dRows - 1)) / dRows / ratio, 220));
    this.root.style.setProperty("--card-w", `${Math.max(44, w)}px`);
    this.root.style.gridTemplateColumns = `repeat(${dCols}, var(--card-w))`;
    this.cards.forEach((el, i) => {
      const { row, col } = rowCol(view, i);
      el.style.gridRow = String((transpose ? col : row) + 1);
      el.style.gridColumn = String((transpose ? row : col) + 1);
    });
  }
}
